import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function Logo({
  className
}: {
  className?: string;
}) {
  return <Link to="/" className={cn("block shrink-0", className)}>
      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556341/img/christian_bak_insignia_logo.png" alt="Christian Bak" className="h-12 w-auto object-contain" />
    </Link>;
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex w-full items-center justify-between px-8">
        <div className="flex gap-8">
          {[1, 2].map(i => <div key={i} className="h-4 w-24 animate-pulse bg-muted/20" />)}
        </div>
        <div className="h-12 w-12 animate-pulse rounded-full bg-muted/20" />
        <div className="flex gap-8">
          {[1, 2].map(i => <div key={i} className="h-4 w-24 animate-pulse bg-muted/20" />)}
        </div>
      </div>;
  }
  const midPoint = Math.ceil(menuItems.length / 2);
  const leftItems = menuItems.slice(0, midPoint);
  const rightItems = menuItems.slice(midPoint);
  const MenuItem = ({
    item
  }: {
    item: any;
  }) => <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link to={item.href || "#"} className="group relative px-2 py-1 text-sm font-medium uppercase tracking-widest text-foreground/90 transition-colors hover:text-primary font-mono">
          {item.label}
          <span className="absolute inset-x-0 bottom-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>;
  return <div className="hidden w-full items-center justify-between lg:flex">
      {}
      <NavigationMenu viewport={false} className="flex-1 justify-end pr-8">
        <NavigationMenuList className="gap-8">
          {leftItems.map(item => <MenuItem key={item.id} item={item} />)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <div className="flex-none px-4">
        <Logo className="h-16 transition-transform duration-300 hover:scale-105" />
      </div>

      {}
      <NavigationMenu viewport={false} className="flex-1 justify-start pl-8">
        <NavigationMenuList className="gap-8">
          {rightItems.map(item => <MenuItem key={item.id} item={item} />)}
        </NavigationMenuList>
      </NavigationMenu>
    </div>;
}
function MobileMenu() {
  const {
    menuItems
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div className="lg:hidden">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Open menu">
        <Menu className="h-6 w-6" />
      </Button>

      {}
      <div className={cn("fixed inset-0 z-[100] flex flex-col bg-background transition-all duration-300", isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none")}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <Logo className="h-10" />
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-foreground hover:bg-destructive/20 hover:text-destructive">
            <X className="h-6 w-6" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-8 overflow-y-auto p-8">
          {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-2xl font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary font-mono" onClick={() => setIsOpen(false)}>
              {item.label}
            </Link>)}
        </div>
        
        <div className="border-t border-border p-6 text-center">
           <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">
             Christian Baks officielle platform
           </p>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-500 dark bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-md" : "bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-8">
        {}
        <div className="flex w-full items-center justify-between lg:hidden">
          <MenuProvider menu_id="44">
            <MobileMenu />
          </MenuProvider>
          
          <div className="absolute left-1/2 -translate-x-1/2">
            <Logo className="h-10" />
          </div>
          
          <div className="w-10" /> {}
        </div>

        {}
        <MenuProvider menu_id="44">
          <DesktopMenu />
        </MenuProvider>
      </div>
    </header>;
}